import React, { useState, useEffect } from "react";
import "./HeroCarousel.css";
import banner1 from "../assests/banner/banner0.jpg";
import banner2 from "../assests/banner/banner1.jpg";
import banner3 from "../assests/banner/banner2.jpg";
import banner4 from "../assests/banner/banner3.png";

// API base — already ends with /api (same constant style as the rest of the site)
const BASE_URL = process.env.REACT_APP_API_URL || "http://192.168.0.57:5001/api";

// Server origin without "/api" — only used if an image path ever comes back relative
const SERVER_ORIGIN = BASE_URL.replace(/\/api\/?$/, "");

// Shown only if the API fails or returns no active banners
const FALLBACK_BANNERS = [
  { id: "local-1", src: banner1, alt: "MKCG Medical College" },
  { id: "local-2", src: banner2, alt: "MKCG Hospital" },
  { id: "local-3", src: banner3, alt: "MKCG Campus" },
  { id: "local-4", src: banner4, alt: "MKCG Campus" },
];

// Full URLs (what /api/banners returns) pass through untouched.
// "images/BannerImage/banner_115.jpeg" -> "<SERVER_ORIGIN>/images/BannerImage/banner_115.jpeg"
function toImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${SERVER_ORIGIN}/${path.replace(/\\/g, "/").replace(/^\/+/, "")}`;
}

export default function HeroCarousel() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  // ── Load banners from DB (tbl_banner) ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/banners`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Banner request failed (${res.status})`);
        const data = await res.json();

        // Existing /api/banners returns [{ id, src, alt }] where src is already a
        // full URL (SITE_BASE + banner_image). Also tolerates { banners: [...] }.
        const rows = Array.isArray(data) ? data : data.banners || [];
        const list = rows
          .filter((b) => b.src)
          .map((b) => ({
            id: b.id,
            src: toImageUrl(b.src),
            alt: b.alt || `MKCG Banner ${b.id}`,
          }));

        if (!cancelled) setBanners(list.length ? list : FALLBACK_BANNERS);
      } catch (err) {
        if (err.name !== "AbortError" && !cancelled) {
          console.error("Could not load banners:", err.message);
          setBanners(FALLBACK_BANNERS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // ── Auto-advance (restarts after every slide change, incl. manual clicks) ──
  useEffect(() => {
    if (banners.length < 2) return;
    const timer = setTimeout(() => setCurrent((c) => (c + 1) % banners.length), 4500);
    return () => clearTimeout(timer);
  }, [current, banners.length]);

  if (loading) return <div className="hero-skeleton skeleton" />;
  if (banners.length === 0) return null;

  const goPrev = () => setCurrent((c) => (c - 1 + banners.length) % banners.length);
  const goNext = () => setCurrent((c) => (c + 1) % banners.length);

  return (
    <div className="hero-carousel">
      <div className="hero-slides">
        {banners.map((b, i) => (
          <div key={b.id} className={`hero-slide${i === current ? " active" : ""}`}>
            <img
              src={b.src}
              alt={b.alt}
              onError={(e) => {
                e.currentTarget.onerror = null; // avoid an endless error loop
                e.currentTarget.src = banner1;
              }}
            />
            <div className="hero-overlay" />
            <div className="hero-caption">
              <p className="hero-tag">Premier Medical Institution of Southern Odisha</p>
              <h2 className="hero-headline">
                Maharaja Krushna Chandra Gajapati
                <br />
                <span className="hero-headline-accent">Medical College &amp; Hospital</span>
              </h2>
              <p className="hero-sub">
                Established 1962 &nbsp;·&nbsp; Berhampur, Odisha &nbsp;·&nbsp; Govt. of Odisha
              </p>
              <div className="hero-chips">
                <span className="hero-chip">🏛️ Govt. of Odisha</span>
                <span className="hero-chip">🎓 250 MBBS Seats/Year</span>
                <span className="hero-chip">🏥 162-Acre Campus</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {banners.length > 1 && (
        <>
          {/* Dots */}
          <div className="hero-dots">
            {banners.map((b, i) => (
              <button
                key={b.id}
                aria-label={`Go to slide ${i + 1}`}
                className={`dot${i === current ? " active" : ""}`}
                onClick={() => setCurrent(i)}
              />
            ))}
          </div>

          {/* Arrows */}
          <button className="hero-arrow left" aria-label="Previous slide" onClick={goPrev}>‹</button>
          <button className="hero-arrow right" aria-label="Next slide" onClick={goNext}>›</button>
        </>
      )}
    </div>
  );
}